// Live updates that never get in the user's way (docs/live-updates-plan.md).
// A page redraws with patchHTML (only what changed), through liveRefresher (waits while
// the user is mid-action) and applyLive (keeps scroll, glows what someone else changed).

import { patchHTML, patchNodes, collectChanges } from './domPatch';
import { onLocalDataChanged, getDB } from './utilities';
import { ref, onChildAdded, onChildChanged, onChildRemoved } from 'firebase/database';
import { database } from './initFirebase';
import { deviceCopy, hasCopy } from './deviceCopy';

export { patchHTML, patchNodes, collectChanges, onLocalDataChanged };

// ---------- is the user in the middle of something? ----------

let pointerDown = false;
let dragging = false;
let lastInputAt = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', () => { pointerDown = true; lastInputAt = Date.now(); }, true);
  document.addEventListener('keydown', () => { lastInputAt = Date.now(); }, true);
  ['pointerup', 'pointercancel'].forEach(t => document.addEventListener(t, () => { pointerDown = false; }, true));
  document.addEventListener('dragstart', () => { dragging = true; }, true);
  ['dragend', 'drop'].forEach(t => document.addEventListener(t, () => { dragging = false; }, true));
}

/** This user clicked or typed within `ms`: a change arriving now is most likely their own. */
export function recentInteraction(ms = 2000) {
  return Date.now() - lastInputAt < ms;
}

/** Mid-click or mid-drag (a patch that doesn't touch fields only needs to wait for this). */
export function pointerHeld() {
  return pointerDown || dragging;
}

/** Pointer held, a drag in progress, or typing in a field (unless it's marked data-live-ok). */
export function userIsBusy() {
  if (pointerDown || dragging) return true;
  const el = document.activeElement;
  return !!(el && el.matches('input, textarea, select, [contenteditable="true"]') && !el.closest('[data-live-ok]'));
}

/**
 * Returns trigger(): runs refresh() now, or once the user is free. busy() adds the page's
 * own reasons to wait (an open popover, dialog or menu). refresh() is never run twice at once.
 */
export function liveRefresher({ refresh, busy = () => false, retryMs = 2000 }) {
  let waiting = false;
  let running = false;
  let again = false;
  const isBusy = () => userIsBusy() || busy();
  async function trigger() {
    if (running) { again = true; return; }
    if (isBusy()) {
      if (!waiting) { waiting = true; setTimeout(() => { waiting = false; trigger(); }, retryMs); }
      return;
    }
    running = true;
    try {
      await refresh(isBusy);
    } catch (err) {
      console.error('[liveView] refresh failed', err);
    } finally {
      running = false;
      if (again) { again = false; trigger(); }
    }
  }
  return trigger;
}

// ---------- redraw without disturbing ----------

export function keepScrollPositions(redraw) {
  const scrolled = [...document.querySelectorAll('*')].filter(el => el.scrollTop || el.scrollLeft)
    .map(el => [el, el.scrollTop, el.scrollLeft]);
  const win = [window.scrollX, window.scrollY];
  try {
    redraw();
  } finally {
    scrolled.forEach(([el, top, left]) => { if (el.isConnected) { el.scrollTop = top; el.scrollLeft = left; } });
    window.scrollTo(win[0], win[1]);
  }
}

/** Redraws for someone else's change: scroll kept, changed data-key items glow (unless glow: false). */
export function applyLive(redraw, { glow: shine = true } = {}) {
  const changed = collectChanges(() => keepScrollPositions(redraw));
  if (shine) glow(changed);
  return changed;
}

const GLOW_CSS = `
@keyframes live-changed-glow {
  0%, 35% { box-shadow: inset 0 0 0 2px rgba(214, 140, 18, 0.95), inset 0 0 18px rgba(214, 140, 18, 0.35); background-color: #FFF6E5; }
  100% { box-shadow: inset 0 0 0 2px rgba(214, 140, 18, 0), inset 0 0 18px rgba(214, 140, 18, 0); }
}
.live-changed { animation: live-changed-glow 2.6s ease-out; }
@keyframes live-changed-svg {
  0%, 35% { filter: drop-shadow(0 0 3px rgba(214, 140, 18, 1)) drop-shadow(0 0 9px rgba(214, 140, 18, 0.85)); }
  100% { filter: drop-shadow(0 0 3px rgba(214, 140, 18, 0)) drop-shadow(0 0 9px rgba(214, 140, 18, 0)); }
}
svg .live-changed { animation-name: live-changed-svg; }`;

function ensureGlowCss() {
  if (document.getElementById('live-changed-css')) return;
  const style = document.createElement('style');
  style.id = 'live-changed-css';
  style.textContent = GLOW_CSS;
  document.head.appendChild(style);
}

/** Brief amber glow on items another user just changed. */
export function glow(elements) {
  const targets = elements.filter(el => el.isConnected && el.dataset && el.dataset.key);
  if (!targets.length) return;
  ensureGlowCss();
  targets.forEach(el => {
    el.classList.remove('live-changed');
    void el.offsetWidth; // restart the animation if it was still running
    el.classList.add('live-changed');
    el.addEventListener('animationend', () => el.classList.remove('live-changed'), { once: true });
  });
}

// ---------- keeping a cached store current ----------

/**
 * Mirrors an RTDB list into an IndexedDB store (records { id: key, ...value }, keyPath 'id'),
 * batching bursts (the first sync replays every child) into one transaction, then calls
 * onFlush(changedIds). For data the shell only copies once at load. Returns an unsubscribe.
 */
export function mirrorToCache(path, store, onFlush) {
  // A section on the change index (deviceCopy.js): catch up on what changed, then hear each
  // change. Child listeners on the section downloaded all of it on every open and on every
  // return to the tab.
  if (hasCopy(path, store)) {
    const copy = deviceCopy(path);
    const ids = new Set();
    let t = null;
    const off = copy.onChange(id => {
      ids.add(id);
      clearTimeout(t);
      t = setTimeout(() => { const list = [...ids]; ids.clear(); onFlush(list); }, 300);
    });
    copy.sync().then(() => onFlush([])).catch(err => console.error(`[liveView] ${path} catch-up failed`, err));
    return () => { off(); clearTimeout(t); };
  }
  const pending = new Map();
  let timer = null;
  const flush = async () => {
    const changes = [...pending];
    pending.clear();
    try {
      const db = await getDB();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        const os = tx.objectStore(store);
        changes.forEach(([id, val]) => (val && typeof val === 'object' ? os.put({ id, ...val }) : os.delete(id)));
        tx.oncomplete = resolve;
        tx.onerror = e => reject(e.target?.error || e);
        tx.onabort = e => reject(e.target?.error || e);
      });
    } catch (err) {
      console.error(`[liveView] ${path} → ${store} failed`, err);
      return;
    }
    onFlush(changes.map(([id]) => id));
  };
  const queue = (id, val) => {
    if (!id || id === 'lastUpdated') return;
    pending.set(id, val);
    clearTimeout(timer);
    timer = setTimeout(flush, 300);
  };
  const r = ref(database, path);
  const offs = [
    onChildAdded(r, snap => queue(snap.key, snap.val())),
    onChildChanged(r, snap => queue(snap.key, snap.val())),
    onChildRemoved(r, snap => queue(snap.key, null)),
  ];
  return () => offs.forEach(off => off());
}

/** Any field inside root the user changed and hasn't saved (value differs from what was drawn). */
export function hasEditedFields(root) {
  if (!root) return false;
  return [...root.querySelectorAll('input, select, textarea')].some(el => {
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked !== el.defaultChecked;
    if (el.tagName === 'SELECT') return [...el.options].some(o => o.selected !== o.defaultSelected);
    return el.value !== el.defaultValue;
  });
}

// ---------- listening only while it's on screen ----------

/**
 * A group of listeners for one open panel. add(subscribe) runs subscribe() (which returns an
 * unsubscribe) now and again after the page comes back from the background; while the page
 * is hidden they're all off, so a phone in a pocket downloads nothing for this panel.
 * close() turns them all off for good (the panel closed). onResume runs after coming back.
 */
export function liveScope(onResume) {
  const subs = [];
  let offs = [];
  let closed = false;
  const stop = () => { offs.forEach(off => { try { off && off(); } catch (e) { /* already off */ } }); offs = []; };
  const start = () => { offs = subs.map(fn => fn()); };
  const onVis = () => {
    if (closed) return;
    if (document.hidden) stop();
    else { stop(); start(); if (onResume) onResume(); }
  };
  document.addEventListener('visibilitychange', onVis);
  return {
    add(subscribe) {
      if (closed) return;
      subs.push(subscribe);
      if (!document.hidden) offs.push(subscribe());
    },
    close() {
      closed = true;
      stop();
      subs.length = 0;
      document.removeEventListener('visibilitychange', onVis);
    },
  };
}
